import { defineComponent as _defineComponent } from 'vue'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download/csv_download.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import api from '@/client/core/api'
import util from '@/client/core/util'
import { formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useNavbarStore } from '@/client/store/useNavbarStore'
import { GetInstagramInstantwinPrizesData, TPrize } from '@/typings/api/instagram_instantwin_prizes.schema'

import { useInstagramInstantwinPrizeStore } from '../hooks/instagramInstantwinPrizeStore'
import { buildInstantwinPrizeKeywordText } from '../utils/prizeKeyword'
import InstagramInstantwinPrizeTable from './InstagramInstantwinPrizeTable.vue'

// Vue 2.7のComposition API でVueインスタンスを取得

export default /*#__PURE__*/_defineComponent({
  __name: 'InstagramInstantwinPrizeMain',
  setup(__props) {

// @ts-nocheck TODO: 既存の型エラーを解消したら外す
const instance = getCurrentInstance()
const $route = instance?.proxy.$route
const $router = instance?.proxy.$router
const navbarStore = useNavbarStore()

const store = useInstagramInstantwinPrizeStore()
const prizeTableRef = ref<{ openCreateDialog: () => void } | null>(null)

const { prizes, error, campaign, firstLoaded, options, params, totalApplicantCount, hasRunningCampaignOnSameAccount } =
  storeToRefs(store)

// computed properties
const campaignStartDatetime = computed(() => {
  if (!campaign.value?.startDatetime) {
    return '-'
  }
  return moment(campaign.value.startDatetime).format('YYYY年MM月DD日 HH:mm')
})

const collectCompletedAt = computed(() => {
  if (!campaign.value?.endDatetime) {
    return '-'
  }
  return moment(campaign.value.endDatetime).add(1, 'hours').format('YYYY年MM月DD日 HH:mm')
})

const isCampaignBeforeStart = computed(() => campaign.value?.status === CAMPAIGN_STATUS.BEFORE_EVENT)
const isCampaignBeforeCollect = computed(() => campaign.value?.status === CAMPAIGN_STATUS.BEFORE_COLLECTION)
const isCampaignFinalCollection = computed(() => campaign.value?.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION)
const isCampaignCollectionCompleted = computed(() => campaign.value?.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED)
const isCreatePrizeDisabled = computed(() => !isCampaignBeforeStart.value)
const canCreatePrize = computed(() => Boolean(campaign.value?.id))

// methods
const onChangePagingNo = (page: number) => {
  if (params.value.pagingNo === page) {
    return
  }
  store.setParams({ pagingNo: page })
  util.scrollToTop()
}

const onChangeLimitCount = (limitCount: number) => {
  if (params.value.limitCount === limitCount) {
    return
  }
  store.setParams({ limitCount })
}

const onChangeSortBy = (sortBy: string) => {
  if (params.value.sortBy === sortBy) {
    return
  }
  store.setParams({ sortBy })
}

const onChangeBackEvent = () => {
  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|戻る')
  $router?.push({ path: '/campaigns' })
}

const onOpenApplicant = (prizeId: number) => {
  $router?.push(`/instagram_instantwins/${campaign.value?.id}/prizes/${prizeId}/applicants`)
}

const onOpenCreatePrizeDialog = () => {
  if (!canCreatePrize.value || isCreatePrizeDisabled.value) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|応募グループの新規作成')
  prizeTableRef.value?.openCreateDialog()
}

const onRefresh = async () => {
  await store.fetchPrizes()
  await store.fetchCampaign()
  setBreadcrumb()
}

const setBreadcrumb = () => {
  if (!campaign.value?.id) {
    return
  }

  navbarStore.setBreadcrumb([
    {
      name: 'キャンペーン一覧',
      link: '/campaigns'
    },
    {
      name: campaign.value.title,
      link: null,
      iconName: campaign.value.account ? campaign.value.account.name : '削除されたアカウント',
      iconUrl: campaign.value.account ? campaign.value.account.pictureUrl : null,
      iconType: campaign.value.snsType,
      isIcon: true,
      label: 'インスタントウィン'
    }
  ])
}

/**
 * CSVダウンロード
 */
const downloadCsv = async () => {
  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|CSVダウンロード')

  const csvParams = Object.assign({}, params.value, {
    forCsv: 1
  })

  const result = await api.get<GetInstagramInstantwinPrizesData>('/instagram_instantwin_prizes', {
    params: csvParams
  })

  if (!result.data) {
    return {
      error: 'ERROR_LOADING'
    }
  }

  const prizesData = result.data.prizes
  const filename = `instagram_instantwin_prize_${campaign.value?.title}_applicants_${moment().format('YYYY_MM_DD')}`
  const csvData: (string | number)[][] = []
  csvData.push(['応募グループ名', '応募キーワード', '応募者数', '当選者数', '当選者数(残り)'])

  // CSVデータを格納
  prizesData.forEach((prize: TPrize) => {
    const keyword = buildInstantwinPrizeKeywordText({
      prizeEntryKeyword: prize.entryKeyword
    })

    csvData.push([
      prize.name,
      keyword,
      prize.applicantCount,
      prize.winnerCount,
      prize.maxWinnerCount - prize.winnerCount
    ])
  })

  return {
    filename,
    data: csvData
  }
}

// lifecycle
onMounted(async () => {
  if (!$route?.params.campaignId) {
    return
  }

  const campaignId = parseInt($route.params.campaignId as string)

  store.setParams({
    campaignId: campaignId
  })

  await store.fetchCampaign()

  if (!campaign.value || !campaign.value.id) {
    return
  }

  setBreadcrumb()

  // 初回データ取得
  await store.fetchPrizes()
})

// パラメータの変更を監視
watch(
  () => params.value,
  () => {
    store.fetchPrizes()
  },
  { deep: true }
)

return { __sfc: true,instance, $route, $router, navbarStore, store, prizeTableRef, prizes, error, campaign, firstLoaded, options, params, totalApplicantCount, hasRunningCampaignOnSameAccount, campaignStartDatetime, collectCompletedAt, isCampaignBeforeStart, isCampaignBeforeCollect, isCampaignFinalCollection, isCampaignCollectionCompleted, isCreatePrizeDisabled, canCreatePrize, onChangePagingNo, onChangeLimitCount, onChangeSortBy, onChangeBackEvent, onOpenApplicant, onOpenCreatePrizeDialog, onRefresh, setBreadcrumb, downloadCsv, CsvDownload, formatNumber, InstagramInstantwinPrizeTable }
}

})